import { apiService } from './api';
import { getActiveLocale, t } from '../i18n/translate';

export interface ArchiveSummary {
  steps_completed: number;
  completed_step_numbers: number[];
  has_getting_started: boolean;
  has_inventories: boolean;
  inventory_count: number;
}

export interface ProgressArchiveListItem {
  archive_id: string;
  archive_name: string;
  created_at: string;
  summary: ArchiveSummary;
  is_auto_archive: boolean;
}

export interface ProgressArchiveDetail extends ProgressArchiveListItem {
  snapshot: Record<string, unknown>;
}

export class ProgressArchiveService {
  static async listArchives(program = 'SAA', track = '12-steps'): Promise<{
    archives: ProgressArchiveListItem[];
    count: number;
    max: number;
    max_with_auto: number;
  }> {
    return apiService.listProgressArchives(program, track);
  }

  static async createArchive(options: {
    archive_name: string;
    clear_active?: boolean;
    is_auto_archive?: boolean;
    program?: string;
    track?: string;
  }): Promise<{
    archive_id: string;
    archive_name: string;
    created_at: string;
    summary: ArchiveSummary;
    cleared: boolean;
  }> {
    return apiService.createProgressArchive(options);
  }

  static async getArchive(archiveId: string, program = 'SAA', track = '12-steps'): Promise<ProgressArchiveDetail> {
    return apiService.getProgressArchive(archiveId, program, track);
  }

  static async restoreArchive(archiveId: string, program = 'SAA', track = '12-steps'): Promise<{ message: string }> {
    return apiService.restoreProgressArchive(archiveId, program, track);
  }

  static async deleteArchive(archiveId: string, program = 'SAA', track = '12-steps'): Promise<void> {
    return apiService.deleteProgressArchive(archiveId, program, track);
  }

  static formatSummary(summary?: ArchiveSummary | null): string {
    if (!summary) return t('archive.summary.snapshot');
    const parts: string[] = [];
    if (summary.has_getting_started) {
      parts.push(t('archive.summary.getting_started'));
    }
    if (summary.steps_completed > 0) {
      parts.push(
        t(summary.steps_completed === 1 ? 'archive.summary.steps_completed_one' : 'archive.summary.steps_completed_other', {
          count: summary.steps_completed,
        })
      );
    }
    if (summary.has_inventories) {
      parts.push(t('archive.summary.inventories_saved'));
    }
    return parts.length > 0 ? parts.join(' · ') : t('archive.summary.snapshot');
  }

  static formatDate(iso: string): string {
    try {
      const locale = getActiveLocale() === 'en' ? 'en-US' : getActiveLocale();
      return new Date(iso).toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return iso;
    }
  }
}
